import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { RelatedLinks } from "@/components/related-links";
import { FaqDetails } from "@/components/faq-details";
import { BurnoutTest } from "@/components/burnout-test";
import { burnoutFaq, METHOD_LONG_NOTE } from "@/content/burnout-test";
import { abs, breadcrumbSchema, PERSON_ID, SITE_URL } from "@/content/seo";
import ogSocial from "@/assets/og-sylwia-sitkowska.jpg";

const title = "Test na wypalenie zawodowe online | Sylwia Sitkowska";
const description =
  "Bezpłatny test na wypalenie zawodowe: 20 pytań, około 3 minuty i wynik od razu, z podziałem na energię, stosunek do pracy, koncentrację i emocje.";
const ogImage = abs(ogSocial);
const PAGE_URL = `${SITE_URL}/test-na-wypalenie-zawodowe`;

const quizJsonLd = {
  "@context": "https://schema.org",
  "@type": "Quiz",
  name: "Test na wypalenie zawodowe",
  description,
  url: PAGE_URL,
  inLanguage: "pl-PL",
  educationalLevel: "beginner",
  educationalUse: "self-assessment",
  author: { "@id": PERSON_ID },
  about: { "@type": "Thing", name: "Wypalenie zawodowe" },
  numberOfQuestions: 20,
  timeRequired: "PT3M",
  isAccessibleForFree: true,
  disclaimer: METHOD_LONG_NOTE,
};

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: burnoutFaq.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a },
  })),
};

const breadcrumbJsonLd = breadcrumbSchema([
  { name: "Strona główna", path: "/" },
  { name: "Psychologia biznesu", path: "/psychologia-biznesu" },
  { name: "Test na wypalenie zawodowe", path: "/test-na-wypalenie-zawodowe" },
]);

export const Route = createFileRoute("/test-na-wypalenie-zawodowe")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: ogImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(quizJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd) },
    ],
  }),
  component: BurnoutTestPage,
});

function BurnoutTestPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="tresc-glowna">
        <section className="border-b border-steel">
          <div className="container-page section-pad">
            <h1 className="serif-display text-[2rem] md:text-[3rem]">
              Test na wypalenie zawodowe
            </h1>
            <p className="measure mt-6 text-base leading-relaxed text-ink/80 md:text-lg">
              Ten test pomaga sprawdzić, jak często pojawiają się u Ciebie sygnały wypalenia
              zawodowego w czterech obszarach: energii, stosunku do pracy, koncentracji oraz
              emocji. Odpowiedzi odnoś do ostatnich tygodni.
            </p>
            

            <div className="mt-10">
              <BurnoutTest />
            </div>
          </div>
        </section>

        <section aria-labelledby="objawy" className="border-b border-steel bg-surface">
          <div className="container-page section-pad">
            <h2 id="objawy" className="display-xl text-[1.7rem] md:text-[2.2rem]">
              Jak rozpoznać wypalenie zawodowe
            </h2>
            <div className="measure mt-6 space-y-4 text-base leading-relaxed text-ink/80">
              <p>
                Światowa Organizacja Zdrowia opisuje wypalenie zawodowe w klasyfikacji ICD-11 jako
                zjawisko zawodowe wynikające z przewlekłego stresu w pracy, z którym nie udało się
                skutecznie sobie poradzić. Wyróżnia trzy obszary: wyczerpanie, dystans psychiczny
                wobec pracy oraz obniżone poczucie skuteczności zawodowej.
              </p>
              <p>
                Objawy wypalenia zawodowego zwykle narastają powoli i przez długi czas dają się
                wytłumaczyć okolicznościami: trudnym kwartałem, reorganizacją, jednym projektem.
                Częściej niepokojące jest to, że sygnały utrzymują się tygodniami i pojawiają się
                w kilku obszarach naraz: w ciele, w nastawieniu do pracy, w koncentracji i w
                relacjach.
              </p>
              <p>
                Więcej o tym, jak to wygląda u osób zarządzających, piszę w tekście o{" "}
                <Link
                  to="/wypalenie-zawodowe-u-lidera"
                  className="border-b border-coral text-ink hover:text-coral-text"
                >
                  wypaleniu zawodowym u lidera
                </Link>
                .
              </p>
            </div>
          </div>
        </section>

        <section aria-labelledby="faq-test" className="border-b border-steel">
          <div className="container-page section-pad">
            <h2 id="faq-test" className="display-xl text-[1.7rem] md:text-[2.2rem]">
              Najczęstsze pytania
            </h2>
            <FaqDetails items={burnoutFaq} className="mt-10" />

          </div>
        </section>
      </main>
      <RelatedLinks
        exclude={["/test-na-wypalenie-zawodowe"]}
        only={["/wypalenie-zawodowe-u-lidera", "/konsultacje"]}
        heading="Jeśli chcesz pójść dalej"
      />
      <SiteFooter />
    </div>
  );
}
